import { useState, useEffect } from 'react';
import axios from '../common/api/request';
import { 
  DesignatedOperatorRelation, 
  DesignatedOperatorRelationsResponse,
  DesignatedOperatorRelationsQueryParams 
} from '../types/designatedOperatorRelations';

// Hook for getting all relations with optional module filter
export function useDesignatedOperatorRelationsAll(module?: string) {
  const [data, setData] = useState<DesignatedOperatorRelationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (module) {
        params.append('module', module);
      }
      
      const response = await axios.get<DesignatedOperatorRelationsResponse>(
        `/designated-operator-relations?${params.toString()}`
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch all designated operator relations:', err);
      setError('Failed to load designated operator relations');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [module]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
}

// Hook for getting relations by year
export function useDesignatedOperatorRelationsByYear(year: number) {
  const [data, setData] = useState<DesignatedOperatorRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<DesignatedOperatorRelation[]>(
        `/designated-operator-relations/year?year=${year}`
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch designated operator relations by year:', err);
      setError('Failed to load designated operator relations for this year');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
}

// Hook for getting relations by year range
export function useDesignatedOperatorRelationsByYearRange(
  startYear: number, 
  endYear: number, 
  module?: string
) {
  const [data, setData] = useState<DesignatedOperatorRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('startYear', startYear.toString());
      params.append('endYear', endYear.toString());
      if (module) {
        params.append('module', module);
      }
      
      const response = await axios.get<DesignatedOperatorRelation[]>(
        `/designated-operator-relations/year-range?${params.toString()}`
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch designated operator relations by year range:', err);
      setError('Failed to load designated operator relations for this year range');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startYear && endYear) {
      fetchData();
    }
  }, [startYear, endYear, module]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
}

// Hook for getting relations by target DO
export function useDesignatedOperatorRelationsByTarget(
  targetDOName: string, 
  module?: string
) {
  const [data, setData] = useState<DesignatedOperatorRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('targetDOName', targetDOName);
      if (module) {
        params.append('module', module);
      }
      
      const response = await axios.get<DesignatedOperatorRelation[]>(
        `/designated-operator-relations/target?${params.toString()}`
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch designated operator relations by target DO:', err);
      setError('Failed to load designated operator relations for this target DO');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (targetDOName) {
      fetchData();
    }
  }, [targetDOName, module]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
}

// Utility hook for getting available years from relations
export function useAvailableYears(module?: string) {
  const { data, isLoading, error } = useDesignatedOperatorRelationsAll(module);
  
  const availableYears = data ? 
    Array.from(new Set(
      Object.values(data.byYear).flat().map(relation => relation.year)
    )).sort((a, b) => b - a) : [];

  return {
    years: availableYears,
    isLoading,
    error
  };
}

// Utility hook for getting available target DOs from relations
export function useAvailableTargetDOs(module?: string) {
  const { data, isLoading, error } = useDesignatedOperatorRelationsAll(module);
  
  const availableTargetDOs = data ? 
    Array.from(new Set(
      Object.values(data.byTargetDO).flat().map(relation => relation.targetDO)
    )).sort() : [];

  return {
    targetDOs: availableTargetDOs,
    isLoading,
    error
  };
}

// Utility hook for getting years for a specific target DO
export function useYearsForTargetDO(targetDOName: string, module?: string) {
  const { data, isLoading, error } = useDesignatedOperatorRelationsByTarget(targetDOName, module);
  
  const years = data ? 
    Array.from(new Set(data.map(relation => relation.year))).sort((a, b) => b - a) : [];

  return {
    years,
    isLoading,
    error
  };
}

// Utility hook for getting target DOs for a specific year
export function useTargetDOsForYear(year: number, module?: string) {
  const { data, isLoading, error } = useDesignatedOperatorRelationsByYear(year);
  
  const targetDOs = data ? 
    Array.from(new Set(data.map(relation => relation.targetDO))).sort() : [];

  return {
    targetDOs,
    isLoading,
    error
  };
}

// Hook for getting relations with both year and target DO filters
export function useDesignatedOperatorRelationsFiltered(
  year?: number,
  targetDO?: string,
  module?: string
) {
  const [data, setData] = useState<DesignatedOperatorRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let response: DesignatedOperatorRelation[] = [];
      
      if (year && targetDO) {
        // If both year and targetDO are provided, get by year and filter by targetDO
        const yearData = await axios.get<DesignatedOperatorRelation[]>(
          `/designated-operator-relations/year?year=${year}${module ? `&module=${module}` : ''}`
        );
        response = yearData.data.filter(relation => relation.targetDO === targetDO);
      } else if (year) {
        // If only year is provided, get by year
        const yearData = await axios.get<DesignatedOperatorRelation[]>(
          `/designated-operator-relations/year?year=${year}${module ? `&module=${module}` : ''}`
        );
        response = yearData.data;
      } else if (targetDO) {
        // If only targetDO is provided, get by targetDO
        const targetData = await axios.get<DesignatedOperatorRelation[]>(
          `/designated-operator-relations/target?targetDOName=${targetDO}${module ? `&module=${module}` : ''}`
        );
        response = targetData.data;
      }
      
      setData(response);
    } catch (err) {
      console.error('Failed to fetch filtered designated operator relations:', err);
      setError('Failed to load designated operator relations');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (year || targetDO) {
      fetchData();
    }
  }, [year, targetDO, module]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
}
